import { faCog } from '@fortawesome/free-solid-svg-icons';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl/dist';
import styled from 'styled-components';

import { AccordionPanel, GIButton } from 'app-components';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { ENROLLMENT_STATUSES } from '../../../../constants/individualPortal/dashboard/dashboardModelTypes';
import { usePermissions } from '../../../../hooks/usePermissions';
import { IEnrollment } from '../common/types';
import useEnrollmentModal from '../hooks/useEnrollmentModal';

const StyledActionsContainer = styled.div`
    .header-container {
        justify-content: end;
        padding: 1rem;
        border-radius: 0;
        svg {
            width: 1.7rem;
        }
        a {
            display: flex;
        }

        .header {
            padding: 2px 0 0 3px;
            width: fit-content;
        }
    }
    .content {
        border: none;
        padding: 10px !important;
    }
    > div {
        border: 0;
    }
`;

const StyledLink = styled.a`
    color: ${({ theme }) => theme.primaryMain} !important;
    display: flex;
    align-items: center;
    font-weight: 600;
`;

const StyledAccordionContent = styled.div`
    display: flex;
    justify-content: center;
`;

interface IEnrollmentHistoryActionsProps {
    enrollment: IEnrollment;
    permissions: string[];
}

const EnrollmentHistoryActions: React.FC<IEnrollmentHistoryActionsProps> = ({ enrollment, permissions }) => {
    const intl = useIntl();
    const { handleOverrideEnrollmentStatus, overrideEnrollment, handleReinstateEnrollment, reinstateenrollment } =
        useEnrollmentModal();

    const handleOverrideEnrollmentStatusClick = useCallback(() => {
        handleOverrideEnrollmentStatus(enrollment, overrideEnrollment);
    }, [enrollment, overrideEnrollment]);

    const handleReinstateEnrollmentClick = useCallback(() => {
        handleReinstateEnrollment(enrollment, reinstateenrollment);
    }, [enrollment, reinstateenrollment]);

    const { isEnrollmentActionsPermission, isReinstatementPermission, isOverideEnrollmentPermission } =
        usePermissions();

    const hasPermission = (status: string[], permissionCheck: (permissions: string[]) => boolean) => {
        return permissionCheck(permissions) && status.includes(enrollment.enrollmentStatus);
    };

    const hasOverrideEnrollmentStatusPermission = () => {
        const isEligibleStatus = [
            ENROLLMENT_STATUSES.PENDING,
            ENROLLMENT_STATUSES.TERMINATED,
            ENROLLMENT_STATUSES.PEND,
        ];
        return hasPermission(isEligibleStatus, isOverideEnrollmentPermission) && !enrollment.enrollmentConfirmationDate;
    };

    const hasReinstateenrollmentPermission = () => {
        const isEligibleStatus = [ENROLLMENT_STATUSES.CANCEL, ENROLLMENT_STATUSES.TERMINATED];
        return hasPermission(isEligibleStatus, isReinstatementPermission);
    };

    return (
        <>
            <ConditionalRendering isTrue={isEnrollmentActionsPermission(permissions, enrollment)}>
                <StyledActionsContainer className="history-actions" data-testid={'enrollmentHistoryActions'}>
                    <AccordionPanel
                        id={'additionnalInfo'}
                        header={<StyledLink> {intl.formatMessage({ id: 'common.actions' })}</StyledLink>}
                        isLeftToggle={true}
                        expandedIcon={faCog}
                        collapsedIcon={faCog}
                        isHeaderClickable={true}
                    >
                        <StyledAccordionContent>
                            <ConditionalRendering isTrue={hasOverrideEnrollmentStatusPermission()}>
                                <GIButton
                                    id="overrideEnrollmentStatus"
                                    onClickHandler={handleOverrideEnrollmentStatusClick}
                                    variant="secondary"
                                    label={intl.formatMessage({ id: 'action.enrollment.overrideEnrollmentStatus' })}
                                ></GIButton>
                            </ConditionalRendering>

                            <ConditionalRendering isTrue={hasReinstateenrollmentPermission()}>
                                <GIButton
                                    id="reinstateenrollment"
                                    onClickHandler={handleReinstateEnrollmentClick}
                                    addlClassNames="btn"
                                    variant="secondary"
                                    label={intl.formatMessage({ id: 'action.enrollment.reinstateenrollment' })}
                                ></GIButton>
                            </ConditionalRendering>
                        </StyledAccordionContent>
                    </AccordionPanel>
                </StyledActionsContainer>
            </ConditionalRendering>
        </>
    );
};

export default EnrollmentHistoryActions;
